import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, ShieldCheck, AlertTriangle, WifiOff, Wifi } from "lucide-react";
import PageHeader from "../components/PageHeader";
import LoadingState from "../components/LoadingState";
import api from "../lib/api";

function fmtDate(iso) {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("it-IT", { timeZone: "Europe/Rome" }); }
    catch { return iso; }
}

function statusBadge(status, lastSyncAt) {
    if (!status || !lastSyncAt) return <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-700">Non sincronizzato</span>;
    const ageHours = (Date.now() - new Date(lastSyncAt).getTime()) / 3600000;
    if (ageHours < 24) return <span data-testid="pki-status-badge" className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#16A34A]/10 text-[#16A34A]">Aggiornato</span>;
    if (ageHours < 48) return <span data-testid="pki-status-badge" className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#F59E0B]/10 text-[#F59E0B]">Da aggiornare</span>;
    return <span data-testid="pki-status-badge" className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-[#DC2626]/10 text-[#DC2626]">Stale</span>;
}

export default function AdminPkiPage() {
    const [status, setStatus] = useState(null);
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [syncing, setSyncing] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const [{ data: st }, { data: lg }] = await Promise.all([
                api.get("/admin/pki/status"),
                api.get("/admin/pki/sync-logs?limit=20"),
            ]);
            setStatus(st);
            setLogs(lg.items || []);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const syncNow = async () => {
        if (!window.confirm("Sincronizzare ora la Trust Service List AgID? L'operazione avviene in background.")) return;
        setSyncing(true);
        try {
            await api.post("/admin/pki/sync");
            toast.success("Sincronizzazione avviata — ricarica tra qualche secondo per vedere i risultati");
            setTimeout(load, 3500);
        } catch (e) { toast.error(e?.response?.data?.detail || "Errore"); }
        finally { setSyncing(false); }
    };

    const testConn = async () => {
        try {
            const { data } = await api.post("/admin/pki/test-connectivity");
            if (data.ok) toast.success(`AgID raggiungibile — ${Math.round(data.size_bytes / 1024)} KB in ${data.elapsed_ms} ms`);
            else toast.error(`AgID non raggiungibile: ${data.error}`);
        } catch (e) { toast.error("Errore"); }
    };

    if (loading || !status) return <LoadingState />;
    const meta = status.metadata || {};
    const providers = meta.providers || [];

    return (
        <div className="space-y-4" data-testid="admin-pki-page">
            <PageHeader
                title="Trust Store PKI"
                subtitle="Sincronizzazione della Trusted Service List AgID (eIDAS)"
                breadcrumbs={[{ label: "Amministrazione" }, { label: "PKI" }]}
                actions={
                    <div className="flex gap-2">
                        <button data-testid="pki-test-connectivity" onClick={testConn}
                                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg border border-[#DCE3EE] bg-white">
                            <Wifi className="w-4 h-4" /> Verifica connettività
                        </button>
                        <button data-testid="pki-sync-now" onClick={syncNow} disabled={syncing}
                                className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-[#2563EB] text-white disabled:opacity-50">
                            <RefreshCw className={`w-4 h-4 ${syncing ? "animate-spin" : ""}`} /> Sincronizza ora
                        </button>
                    </div>
                }
            />

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                    <div className="text-xs text-[#64748B]">Stato</div>
                    <div className="mt-2">{statusBadge(status.last_sync_log?.status, meta.last_sync_at)}</div>
                    <div className="text-[11px] text-[#64748B] mt-2" data-testid="pki-last-sync">
                        {fmtDate(meta.last_sync_at)}
                    </div>
                </div>
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                    <div className="text-xs text-[#64748B]">Versione TSL</div>
                    <div className="text-2xl font-semibold" data-testid="pki-tsl-version">v{meta.tsl_version || "—"}</div>
                    <div className="text-[11px] text-[#64748B]">Sequenza {meta.sequence_number || "—"}</div>
                </div>
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                    <div className="text-xs text-[#64748B]">Gestori PEC / CA</div>
                    <div className="text-2xl font-semibold text-[#2563EB]" data-testid="pki-providers-count">{meta.providers_count || 0}</div>
                </div>
                <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                    <div className="text-xs text-[#64748B]">Certificati</div>
                    <div className="text-2xl font-semibold text-[#16A34A]" data-testid="pki-certs-count">{meta.certs_total || 0}</div>
                </div>
            </div>

            <div className="bg-white border border-[#DCE3EE] rounded-xl p-4">
                <div className="text-xs text-[#64748B]">PKI Mode attuale</div>
                <div className="mt-1 font-mono text-sm" data-testid="pki-mode">{status.pki_mode}</div>
                <div className="text-[11px] text-[#64748B] mt-1">
                    Modifica via env <code>PKI_MODE</code> (valori: <code>agid</code>, <code>test</code>, <code>both</code>). Default <code>both</code> in dev.
                </div>
            </div>

            <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-[#DCE3EE] font-semibold text-sm">Provider caricati</div>
                <table className="w-full text-sm" data-testid="pki-providers-table">
                    <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                        <tr>
                            <th className="text-left px-4 py-2">Nome</th>
                            <th className="text-left px-4 py-2">Servizi</th>
                            <th className="text-left px-4 py-2">Certificati</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#DCE3EE]">
                        {providers.map((p) => (
                            <tr key={p.slug} data-testid={`pki-provider-${p.slug}`}>
                                <td className="px-4 py-2 text-xs font-medium">{p.name}</td>
                                <td className="px-4 py-2 text-xs">{p.services_count}</td>
                                <td className="px-4 py-2 text-xs">{p.certs_count}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <div className="bg-white border border-[#DCE3EE] rounded-xl overflow-hidden">
                <div className="px-4 py-3 border-b border-[#DCE3EE] font-semibold text-sm">Log di sincronizzazione</div>
                <table className="w-full text-sm" data-testid="pki-sync-logs-table">
                    <thead className="bg-[#F5F7FB] text-[11px] uppercase text-[#64748B]">
                        <tr>
                            <th className="text-left px-4 py-2">Iniziato</th>
                            <th className="text-left px-4 py-2">Fine</th>
                            <th className="text-left px-4 py-2">Stato</th>
                            <th className="text-left px-4 py-2">Provider</th>
                            <th className="text-left px-4 py-2">Certs</th>
                            <th className="text-left px-4 py-2">Errore</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-[#DCE3EE]">
                        {logs.length === 0 && <tr><td colSpan="6" className="p-3 text-center text-[#64748B]">Nessun log</td></tr>}
                        {logs.map((l) => (
                            <tr key={l.id}>
                                <td className="px-4 py-2 text-xs text-[#64748B]">{fmtDate(l.started_at)}</td>
                                <td className="px-4 py-2 text-xs text-[#64748B]">{fmtDate(l.finished_at)}</td>
                                <td className="px-4 py-2 text-xs">
                                    <span className={l.status === "success" ? "text-[#16A34A]" : l.status === "error" ? "text-[#DC2626]" : "text-[#F59E0B]"}>
                                        {l.status}
                                    </span>
                                </td>
                                <td className="px-4 py-2 text-xs">{l.providers_updated ?? "—"}</td>
                                <td className="px-4 py-2 text-xs">{l.certs_total ?? "—"}</td>
                                <td className="px-4 py-2 text-[11px] text-[#DC2626] max-w-md truncate">{l.error_detail || ""}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
